import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline:
					'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline'
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				icon: 'h-10 w-10'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);
// Extend ButtonProps to include disabledTooltip
export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	formAction?: any;
	disabledTooltip?: string; // Add optional disabledTooltip prop
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, disabledTooltip, disabled, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';

		return (
			<div className="relative group">
				{/* Conditionally render the tooltip when button is disabled and tooltip is provided */}
				{disabled && disabledTooltip && (
					<div className="w-50 absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
						{disabledTooltip}
					</div>
				)}
				<Comp
					className={cn(buttonVariants({ variant, size, className }))}
					ref={ref}
					disabled={disabled} // Keep the disabled property intact
					{...props}
				/>
			</div>
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
